import React, { useEffect, useMemo, useState } from 'react';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';
import { SparklesIcon } from '../../navbar/icons';

/**
 * AIReviewLoadingComposer
 *
 * Replaces the normal composer (quick-prompts + input box) while
 * the AI review mode is initialising. Shows an animated loading
 * indicator so the user knows the review is being prepared.
 */
const PHASES = [
  { label: 'Starting review', durationMs: 2500 },
  { label: 'Analysing your website', durationMs: null },
];

const AIReviewLoadingComposer = () => {
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [dotCount, setDotCount] = useState(1);

  // Animate dots: . → .. → ... (loop)
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev >= 3 ? 1 : prev + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Advance phase after the duration of the current phase
  useEffect(() => {
    const currentPhase = PHASES[phaseIndex];
    if (!currentPhase?.durationMs) return;

    const timeout = setTimeout(() => {
      setPhaseIndex((prev) => Math.min(prev + 1, PHASES.length - 1));
    }, currentPhase.durationMs);

    return () => clearTimeout(timeout);
  }, [phaseIndex]);

  const label = useMemo(() => {
    const phase = PHASES[phaseIndex] || PHASES[PHASES.length - 1];
    return `${phase.label}${'.'.repeat(dotCount)}`;
  }, [phaseIndex, dotCount]);

  return (
    <div className={`p-3 border-t ${borders.default} ${surfaces.primary}`}>
      {/* Review-mode green tint overlay */}
      <div className="relative">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-lg bg-green-400/10"
        />
        <div
          className={`relative z-10 flex items-center justify-center gap-3 rounded-lg border ${borders.default} px-4 py-5`}
        >
          <div className="shrink-0 animate-spin">
            <SparklesIcon className="w-5 h-5 text-green-400" />
          </div>
          <span className={`${typography.bodySmall} ${textColors.secondary}`}>
            {label}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AIReviewLoadingComposer;
